import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { CheckCircle, Circle, Sparkles, ChevronRight, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { auth } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Progress {
  completed?: number;
  total?: number;
  percentage?: number;
}

/**
 * PhaseManager - World Class Minimalist Design
 */
// Translation map for existing Norwegian phase names
const norwegianToEnglishPhases: Record<string, string> = {
  'Innledende analyse': 'Initial Analysis',
  'Saksforberedelse': 'Case Preparation',
  'Bevisinnhenting': 'Evidence Gathering',
  'Forhandlinger': 'Negotiations',
  'Rettslig prosess': 'Legal Process',
  'Avslutning': 'Conclusion',
  'Dokumentgjennomgang': 'Document Review',
  'Klientmøte': 'Client Meeting',
  'Juridisk research': 'Legal Research',
  'Utkast til dokumenter': 'Document Drafting',
  'Gjennomgang av kontrakt': 'Contract Review',
  'Due diligence': 'Due Diligence',
  'Risikovurdering': 'Risk Assessment',
  'Sluttføring': 'Finalization',
  'Oppfølging': 'Follow-up',
  'Mekling': 'Mediation',
  'Anke': 'Appeal',
  'Tvisteløsning': 'Dispute Resolution',
};

const PhaseManager = ({ caseId, caseData }: { caseId: string; caseData?: any }) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'no';
  const isEnglish = currentLanguage === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;
  
  // Helper function to translate phase names
  const translatePhaseName = (name: string): string => {
    if (!isEnglish) return name;
    return norwegianToEnglishPhases[name] || name;
  };
  
  const [phases, setPhases] = useState<any[]>([]);
  const [progress, setProgress] = useState<Progress>({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [newPhase, setNewPhase] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchPhases();
  }, [caseId]);

  const fetchPhases = async () => {
    try {
      const response = await axios.get(`${API}/phases/${caseId}`);
      setPhases(response.data.phases || []);
      setProgress(response.data.progress || {});
    } catch (error) {
      console.error('Error fetching phases:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePhases = async () => {
    setGenerating(true);
    try {
      const lang = isEnglish ? 'en' : 'no';
      const response = await axios.post(`${API}/phases/generate?case_id=${caseId}&language=${lang}`);
      
      if (response.data.phases && response.data.phases.length > 0) {
        toast.success(t(`${response.data.count} phases generated by AI!`, `${response.data.count} faser generert av AI!`));
        fetchPhases();
      } else {
        toast.info(response.data.message || t('No phases generated', 'Ingen faser generert'));
      }
    } catch (error) {
      console.error('Error generating phases:', error);
      toast.error(t('Could not generate phases', 'Kunne ikke generere faser'));
    } finally {
      setGenerating(false);
    }
  };

  const updatePhaseStatus = async (phaseId, newStatus) => {
    try {
      await axios.patch(`${API}/phases/${phaseId}`, { status: newStatus });
      toast.success(t('Phase updated!', 'Fase oppdatert!'));
      fetchPhases();
    } catch (error) {
      console.error('Error updating phase:', error);
      toast.error(t('Could not update phase', 'Kunne ikke oppdatere fase'));
    }
  };

  const addManualPhase = async () => {
    if (!newPhase.name.trim()) {
      toast.error(t('Phase must have a name', 'Fase må ha et navn'));
      return;
    }

    try {
      const nextOrder = phases.length + 1;
      await axios.post(`${API}/phases/`, {
        case_id: caseId,
        name: newPhase.name,
        description: newPhase.description,
        order: nextOrder
      });
      
      toast.success(t('Phase added!', 'Fase lagt til!'));
      setNewPhase({ name: '', description: '' });
      setShowAddPhase(false);
      fetchPhases();
    } catch (error) {
      console.error('Error adding phase:', error);
      toast.error(t('Could not add phase', 'Kunne ikke legge til fase'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  const completedCount = phases.filter(p => p.status === 'COMPLETED').length;
  const inProgressCount = phases.filter(p => p.status === 'IN_PROGRESS').length;

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-5xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{t('Phases', 'Faser')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('Structure the case in logical work phases', 'Strukturer saken i logiske arbeidsfaser')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {phases.length === 0 ? (
                <Button
                  onClick={generatePhases}
                  disabled={generating}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-xl"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('Generating...', 'Genererer...')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('Generate with AI', 'Generer med AI')}
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => setShowAddPhase(true)}
                  variant="outline"
                  className="rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Add phase', 'Legg til fase')}
                </Button>
              )}
            </div>
          </div>

          {/* Stats Row */}
          {phases.length > 0 && (
            <div className="flex items-center gap-12 py-5 border-y border-gray-100">
              <div>
                <div className="text-2xl font-semibold text-gray-900">{phases.length}</div>
                <div className="text-xs text-gray-400 mt-0.5">{t('Total phases', 'Totalt faser')}</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-emerald-600">{completedCount}</div>
                <div className="text-xs text-gray-400 mt-0.5">{t('Completed', 'Fullført')}</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-violet-600">{inProgressCount}</div>
                <div className="text-xs text-gray-400 mt-0.5">{t('In progress', 'Pågår')}</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-gray-900">{progress.percentage || 0}%</div>
                <div className="text-xs text-gray-400 mt-0.5">{t('Progress', 'Framgang')}</div>
              </div>
            </div>
          )}
        </header>

        {/* Progress Bar */}
        {phases.length > 0 && (
          <div className="mb-8">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gray-900 transition-all duration-500 rounded-full"
                style={{ width: `${progress.percentage || 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Empty State */}
        {phases.length === 0 && (
          <div className="text-center py-16">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-900 font-medium mb-1">{t('No phases yet', 'Ingen faser ennå')}</p>
            <p className="text-sm text-gray-500 mb-4">
              {t('Let AI structure the case in logical phases', 'La AI strukturere saken i logiske faser')}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={generatePhases}
                disabled={generating}
                className="bg-gray-900 hover:bg-gray-800 rounded-xl"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {t('Generate with AI', 'Generer med AI')}
              </Button>
              <Button
                onClick={() => setShowAddPhase(true)}
                variant="outline"
                className="rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('Add manually', 'Legg til manuelt')}
              </Button>
            </div>
          </div>
        )}

        {/* Add Phase Form */}
        {showAddPhase && (
          <div className="mb-6 p-6 bg-gray-50 rounded-2xl border border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-4">{t('Add phase', 'Legg til fase')}</h3>
            <div className="space-y-4">
              <input
                type="text"
                value={newPhase.name}
                onChange={(e) => setNewPhase({...newPhase, name: e.target.value})}
                placeholder={t('Phase name', 'Fasenavn')}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
              />
              <textarea
                value={newPhase.description}
                onChange={(e) => setNewPhase({...newPhase, description: e.target.value})}
                placeholder={t('Description (optional)', 'Beskrivelse (valgfritt)')}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm resize-none"
              />
              <div className="flex gap-2">
                <Button onClick={addManualPhase} className="bg-gray-900 hover:bg-gray-800 rounded-xl">
                  {t('Add', 'Legg til')}
                </Button>
                <Button variant="outline" onClick={() => { setShowAddPhase(false); setNewPhase({ name: '', description: '' }); }} className="rounded-xl">
                  {t('Cancel', 'Avbryt')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Phase List */}
        {phases.length > 0 && (
          <div className="space-y-0">
            {phases.map((phase, index) => {
              const isCompleted = phase.status === 'COMPLETED';
              const isInProgress = phase.status === 'IN_PROGRESS';
              const isNotStarted = phase.status === 'NOT_STARTED';
              
              return (
                <div
                  key={phase.id}
                  className={`group flex items-center gap-4 py-4 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg ${
                    isInProgress ? 'bg-violet-50/50' : ''
                  }`}
                >
                  {/* Phase number/check */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-semibold ${
                    isCompleted 
                      ? 'bg-emerald-500 text-white'
                      : isInProgress 
                      ? 'bg-violet-600 text-white'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : phase.order}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-medium text-sm ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {translatePhaseName(phase.name)}
                    </h4>
                    {phase.description && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{phase.description}</p>
                    )}
                  </div>
                  
                  {/* Status badge */}
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    isCompleted ? 'bg-emerald-100 text-emerald-700' :
                    isInProgress ? 'bg-violet-100 text-violet-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {isCompleted ? t('Completed', 'Fullført') : isInProgress ? t('In progress', 'Pågår') : t('Not started', 'Ikke startet')}
                  </span>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isNotStarted && (
                      <Button
                        onClick={() => updatePhaseStatus(phase.id, 'IN_PROGRESS')}
                        size="sm"
                        className="bg-gray-900 hover:bg-gray-800 rounded-lg text-xs"
                      >
                        {t('Start', 'Start')}
                      </Button>
                    )}
                    {isInProgress && (
                      <Button
                        onClick={() => updatePhaseStatus(phase.id, 'COMPLETED')}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-lg text-xs"
                      >
                        {t('Complete', 'Fullfør')}
                      </Button>
                    )}
                  </div>
                  
                  <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-gray-400 transition-colors" />
                </div>
              );
            })}
          </div>
        )}

        {/* AI Suggestion */}
        {phases.length > 0 && phases.filter(p => p.status !== 'COMPLETED').length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-medium text-gray-400">{t('Lexy suggests', 'Lexy foreslår')}</span>
              </div>
              <p className="text-sm text-gray-300">
                {inProgressCount === 0 
                  ? t('Start with the first phase to get started with the case.', 'Start med den første fasen for å komme i gang med saken.')
                  : (isEnglish 
                      ? `You have ${phases.filter(p => p.status === 'NOT_STARTED').length} phases not started. Complete the ongoing phase before starting new ones.`
                      : `Du har ${phases.filter(p => p.status === 'NOT_STARTED').length} faser som ikke er startet. Fullfør pågående fase før du starter nye.`)
                }
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhaseManager;
